import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { DataService } from '@app-core/services/data/data.service';
import { DateService } from '@app-core/services/date/date.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { UpdateTripsFilter } from '@app-trips/actions/trip-filter.actions';
import { UpdateTripsTablePageIndex, UpdateTripsTableSort } from '@app-trips/actions/trips-table.actions';
import { AssetStatsParams, DriverStatsParams, FleetStatsParams } from '@app-trips/common/trips.model';
import { getTripsFilter, getTripsState, getTripsTableState, State, TripsFilterState, TripsTableState } from '@app-trips/reducers';
import { TripsService } from '@app-trips/services/trips.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { KeyboardShortcutsService } from '@app-core/services/keyboard-shortcuts/keyboard-shortcuts.service';
import { KEYBOARD_SHORTCUTS } from '@app-core/constants/keyboard-shortcuts.constants';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { TripListComponent } from '@app-trips/components/trip-list/trip-list.component';
import { AsyncReportType, BREAKPOINTS_LANDSCAPE, BREAKPOINTS_MEDIUM, BREAKPOINTS_SMALL } from '@app-core/constants/constants';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { RequestDownloadReportBody } from '@app-core/models/core.model';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

const tabNameIndexMapping = {
  trips: 0,
  'active-drivers': 1,
  manage: 2,
};

interface TripsDownloadReportBody extends RequestDownloadReportBody {
  dateFormat?: string;
  unit?: string;
  assetId?: string;
  driverId?: string;
  includeInsignificantTrips?: boolean;
}
@Component({
  selector: 'app-trips',
  templateUrl: './trips.component.html',
  styleUrls: ['./trips.component.scss'],
})
export class TripsComponent implements OnInit, OnDestroy {
  public fleetId: string;
  public tripsTableState: TripsTableState = {} as TripsTableState;
  public tripsFilterState: TripsFilterState = {} as TripsFilterState;
  public tripsCount = 0;
  public currentMetricUnit = null;
  public loader = false;
  public currentTabIndex = 0;
  public keyboardShortcuts = KEYBOARD_SHORTCUTS;
  public currentOS = 'windows';
  public currentDateFormat: string;
  public isMobile: boolean;
  public entityType = ['trip', 'asset', 'driver'];
  public csvDownloadSuccess: boolean = false;
  public csvDownloadLoader = false;
  @ViewChild(TripListComponent) tripListComponent: TripListComponent;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private unsubscribeStatsApi: Subject<void> = new Subject<void>();

  constructor(
    public dataService: DataService,
    private gtmService: GoogleTagManagerService,
    private store: Store<State>,
    private tripsService: TripsService,
    private dateService: DateService,
    private router: Router,
    private route: ActivatedRoute,
    private snackbarService: SnackBarService,
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private keyboardShortcutsService: KeyboardShortcutsService,
    private asyncDownloadsService: AsyncDownloadsService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit() {
    this.breakpointObserver
      .observe([...BREAKPOINTS_SMALL, ...BREAKPOINTS_MEDIUM, ...BREAKPOINTS_LANDSCAPE])
      .subscribe((state: BreakpointState) => {
        if (state.matches && this.router.url === '/trips') {
          this.isMobile = state.matches;
        }
      });

    this.store
      .select(getTripsFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((tripsFilterState) => {
        this.tripsFilterState = tripsFilterState;
      });

    this.store
      .select(getTripsTableState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((tripsTableFilterState) => {
        this.tripsTableState = tripsTableFilterState;
      });

    this.store
      .select(getTripsState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.unsubscribeStatsApi.next();
        this.updateTripCount();
      });

    this.dataService._currentMetricUnit.subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
      }
    });

    this.dataService._currentDateFormat.subscribe((value: string) => {
      if (value) {
        this.currentDateFormat = value;
      }
    });

    this.dataService._currentFleet.subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
        this.updateTripCount();
      }
    });

    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((param: any) => {
      const currentTab = param.tab;
      this.currentTabIndex = tabNameIndexMapping[currentTab] || 0;
      if (this.currentTabIndex === 0) {
        this.gtmService.customTabs('/trips', 'Trips', 'Trips');
      } else if (this.currentTabIndex === 1) {
        this.gtmService.customTabs('/trips', 'Trips', 'Active Drivers');
      } else {
        this.gtmService.customTabs('/trips', 'Trips', 'Manage');
      }
    });

    this.currentOS = this.dataService.getCurrentOS();
    this.configureKeyboardShortcuts();
  }

  public onPageTabChange(event: MatTabChangeEvent) {
    const [tabName = 'trips'] = Object.entries(tabNameIndexMapping).find(([, index]) => index === event.index);
    this.router.navigate([], {
      queryParams: {
        tab: tabName,
      },
    });
    let displayTabName = '';
    if (tabName === 'trips') {
      displayTabName = 'Trips tab clicked - Trips Page';
    } else if (tabName === 'active-drivers') {
      displayTabName = 'Active Drivers Tab Clicked - Trips Page';
    } else {
      displayTabName = 'Manage Tab Clicked - Trips Page';
    }
    this.mixpanelService.track(`${displayTabName}`, {
      'Tab Name': tabName,
    });
  }
  public onRefresh(): void {
    if (this.tripListComponent) {
      this.tripListComponent.getTripList(true);
    }
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.unsubscribeStatsApi.next();
    this.unsubscribeStatsApi.complete();
  }

  public onTripFilterChange(tripFilter: any) {
    const {
      driverId: newDriverId = '',
      paramStartDate: newStartDate = '',
      paramEndDate: newEndDate = '',
      displayStartDate = null,
      displayEndDate = null,
      asset: newAsset = null,
      filterType: newFilterType = 'driverFilter',
    } = tripFilter || {};
    const {
      driverId: oldDriverId = '',
      paramStartDate: oldStartDate = '',
      paramEndDate: oldEndDate = '',
      asset: oldAsset = null,
      filterType: oldFilterType = 'driverFilter',
    } = this.tripsFilterState;
    const isDriverIdChanged = newDriverId !== oldDriverId;
    const isTimeRangeChanged = newStartDate !== oldStartDate || newEndDate !== oldEndDate;
    const isAssetIdChanged = newAsset?.assetId !== oldAsset?.assetId;
    const isFilterTypeChanged = newFilterType !== oldFilterType;
    if (newFilterType === 'driverFilter' && isDriverIdChanged) {
      this.gtmService.changeTripListDriverFilter(newDriverId || 'All Drivers');
      this.mixpanelService.track('change Trip List Driver Filter', {
        'Driver Id': newDriverId || 'All Drivers',
      });
    }
    if (isTimeRangeChanged) {
      const noOfDays = this.dateService.getNoOfDays(newStartDate, newEndDate);
      const durationInText = this.dateService.getDurationText(displayStartDate, displayEndDate, noOfDays);
      if (this.currentTabIndex === 0) {
        this.gtmService.changeTripListDurationFilter(durationInText, noOfDays);
        this.mixpanelService.track('change Trip List Duration Filter', {
          Duration: durationInText,
          'Number Of Days': noOfDays,
        });
      } else {
        this.gtmService.changeActiveDriversListDurationTypeFilter(durationInText, noOfDays);
      }
    }
    if (newFilterType === 'assetFilter' && isAssetIdChanged) {
      this.gtmService.changeTripListAssetTypeFilter(newAsset);
      this.mixpanelService.track('change Trip List Asset Type Filter', {
        'Asset Id': newAsset,
      });
    }
    if (isFilterTypeChanged) {
      this.gtmService.changeTripListFilterTypeFilter(newFilterType);
      this.mixpanelService.track('change Trip List Fliter Type', {
        'Filter Type': newFilterType,
      });
    }
    this.store.dispatch(new UpdateTripsFilter(tripFilter));
  }

  public onTripsTablePageChange(event: PageEvent) {
    this.gtmService.tripListPageChange(event);
    this.mixpanelService.track('Change Trip List table page', {
      Event: event,
    });
    this.store.dispatch(new UpdateTripsTablePageIndex({ pageIndex: event.pageIndex }));
  }

  public onTripsTableSortChange(event: Sort) {
    this.store.dispatch(
      new UpdateTripsTableSort({
        sortKey: event.active,
        sortDirection: event.direction,
      })
    );
    this.gtmService.sortTripList(event);
    this.mixpanelService.track('Sort Trip List', {
      Event: event,
    });
  }

  public pushTripDetailsEvent(trip: any) {
    if (!trip) {
      return;
    }
  }

  private updateTripCount() {
    this.loader = true;
    this.getStatsApiObservable()
      .pipe(
        takeUntil(this.unsubscribeStatsApi),
        finalize(() => (this.loader = false))
      )
      .subscribe(
        (res = {} as any) => {
          this.tripsCount = res.aggregate.tripCount || 0;
        },
        () => {
          this.tripsCount = 0;
        }
      );
  }

  private getStatsApiObservable() {
    const { driverId, asset, paramStartDate, paramEndDate, tagIds } = this.tripsFilterState;

    if (asset?.assetId) {
      const assetStatsParams = new AssetStatsParams({
        fleetId: this.fleetId,
        assetId: asset?.assetId,
        startDate: paramStartDate,
        endDate: paramEndDate,
        unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
      });
      if (tagIds && tagIds.length) {
        assetStatsParams['tagIds[]'] = tagIds;
        this.mixpanelService.track('Tag Id', {
          'Tag Id': tagIds,
        });
      }
      return this.tripsService.getAssetStats(assetStatsParams);
    }

    if (driverId) {
      const driverStatsParams = new DriverStatsParams({
        fleetId: this.fleetId,
        driverId,
        startDate: paramStartDate,
        endDate: paramEndDate,
        includeEventDiff: true,
        unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
      });
      if (tagIds && tagIds.length) {
        driverStatsParams['tagIds[]'] = tagIds;
        this.mixpanelService.track('Tag Id', {
          'Tag Id': tagIds,
        });
      }
      return this.tripsService.getDriverStats(driverStatsParams);
    }
    const fleetStatsParams = new FleetStatsParams({
      fleetId: this.fleetId,
      startDate: paramStartDate,
      endDate: paramEndDate,
      includeEventDiff: true,
      unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
    });
    if (tagIds && tagIds.length) {
      fleetStatsParams['tagIds[]'] = tagIds;
      this.mixpanelService.track('Tag Id', {
        'Tag Id': tagIds,
      });
    }
    return this.tripsService.getFleetStats(fleetStatsParams);
  }

  public configureKeyboardShortcuts() {
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab0[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(0);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab1[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(1);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab2[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(2);
      });
  }

  public onKeyboardTabChange(tabIndex: number) {
    const [tabName = 'trips'] = Object.entries(tabNameIndexMapping).find(([, idx]) => idx === tabIndex);
    this.router.navigate([], {
      queryParams: {
        tab: tabName,
      },
    });
    let displayTabName = '';
    if (tabName === 'trips') {
      displayTabName = 'Trips tab clicked - Trips Page';
    } else if (tabName === 'active-drivers') {
      displayTabName = 'Active Drivers Tab Clicked - Trips Page';
    } else {
      displayTabName = 'Manage Tab Clicked - Trips Page';
    }
    this.mixpanelService.track(`${displayTabName}`, {
      'Tab Name': tabName,
    });
  }

  public exportTrips() {
    this.csvDownloadSuccess = null;
    const { driverId, asset, paramStartDate, paramEndDate, tagIds, includeInsignificantTrips } = this.tripsFilterState;
    const body: TripsDownloadReportBody = {
      reportType: 'TRIPS',
      startDate: paramStartDate,
      endDate: paramEndDate,
      dateFormat: this.currentDateFormat,
      unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
      ...(asset ? { assetId: asset?.assetId } : {}),
      ...(driverId ? { driverId } : {}),
      ...(includeInsignificantTrips ? { includeInsignificantTrips } : {}),
    };
    this.csvDownloadLoader = true;
    if (tagIds && tagIds.length) {
      body['tagIds[]'] = tagIds;
    }
    this.gtmService.exportTripsCSV(this.fleetId);
    this.asyncDownloadsService
      .requestDownloadReport(body)
      .pipe(
        finalize(() => (this.csvDownloadLoader = false)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.csvDownloadSuccess = true;
        },
        (err: HttpErrorResponse) => {
          this.csvDownloadSuccess = false;
          if (err?.status === 409) {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  public schedule() {
    this.mixpanelService.track('Schedule Clicked- From Trips Export Option', {
      'Report Type': AsyncReportType.trips.key,
    });
    const { includeInsignificantTrips = false } = this.tripsFilterState;
    this.asyncDownloadsService.navigateToCreateSchedule(AsyncReportType.trips.key, { includeInsignificantTrips });
  }

  public bulkEditTrip() {
    this.router.navigate(['/trips/bulk-edit-trips']);
    this.mixpanelService.track('Bulk Edit Clicked - Trips Tab / Trips Page');
  }

  public tripCountEmit(tripsCount: number) {
    this.tripsCount = tripsCount;
  }

  public navigateToDownloads() {
    this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.trips.key);
    this.mixpanelService.track('Export Options Clicked - Trips Tab / Trips Page');
  }
}
